/* KODA Specification Sheet — variant PDF generator */
/**
 * koda-sheet-core.js
 * Pure, environment-agnostic layout/render for the KODA Specification Sheet PDF.
 * Takes a jsPDF `doc`, a plain `data` model, and a `cfg` of tunables.
 * No DOM, no window, no image loading — the browser entry (koda-variant-sheet.js)
 * scrapes the DOM into `data` and pre-loads images; the Node harness feeds mock data.
 *
 * Works in Node (module.exports) and the browser (window.KodaSheet).
 */
(function (root, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.KodaSheet = api;
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  // ── Layout geometry (mm) — matches the original sheet ──────────────────────
  const L = {
    margin: 15, pageW: 297, pageH: 210,
    get textWidth() { return this.pageW - this.margin * 2; }, // 267
    maxY: 180,                 // content boundary; footer lives below
    col1: { w: 70 }, col2: { w: 72 }, colGap: 8,
    get col3W() { return this.textWidth - this.col1.w - this.col2.w - this.colGap * 2; }, // 109
    get col1X() { return this.margin; },
    get col2X() { return this.col1X + this.col1.w + this.colGap; },
    get col3X() { return this.col2X + this.col2.w + this.colGap; },
  };

  const KODA_ORANGE = [248, 157, 60];
  const PT2MM = 0.352778;
  const CFG_VAL_FACTOR = 1.7; // line spacing for config option values (room for chips)

  // ── Default tunables ───────────────────────────────────────────────────────
  const DEFAULTS = {
    showAllOptions: true,        // list every option per group, highlight selected
    selectedColor: KODA_ORANGE,  // highlight colour for the selected option
    unselectedColor: [150, 150, 150],
    logoLink: 'https://kodalighting.com/', // header logo → website (change to .com.au if needed)
    descMaxChars: 400,           // description hard cap (cut on word boundary)
    col1ImgH: 62,                // fixed height per product image box (mm)
    col1MaxImages: 2,
    // overflow strategy for column 3
    slightOverflowMm: 18,        // <= this much overflow → compress instead of paginate
    techLineFactorFull: 1.35,    // normal line spacing
    techLineFactorTight: 1.08,   // compressed line spacing
    techCharSpaceTight: -0.12,   // negative tracking when compressed (mm)
    footerAddress: [
      'Sydney: 21/198-222 Young Street Waterloo New South Wales 2017 | Phone: 02 9699 6007',
      'Melbourne Lightspace: 2/151 Barkly Avenue, Burnley VIC 3121 | Phone: 03 7064 5130',
      'Email: info@kodalighting.com.au',
    ],
  };

  // ── Small text helpers ─────────────────────────────────────────────────────
  const lh = (pt, factor) => pt * PT2MM * factor;

  function truncateSmart(str, max) {
    str = (str || '').replace(/\s+/g, ' ').trim();
    if (str.length <= max) return str;
    let slice = str.slice(0, max);
    const lastSpace = slice.lastIndexOf(' ');
    if (lastSpace > max * 0.6) slice = slice.slice(0, lastSpace);
    return slice.replace(/[\s,.;:–\-]+$/, '') + '…';
  }

  // Wrap text to width, force-breaking tokens with no spaces (long SKUs).
  function wrapHard(doc, text, width) {
    const out = [];
    (text || '').split('\n').forEach((para) => {
      const words = para.split(' ');
      let line = '';
      const pushWord = (w) => {
        const test = line ? line + ' ' + w : w;
        if (doc.getTextWidth(test) <= width) { line = test; return; }
        if (line) { out.push(line); line = ''; }
        // word itself may exceed width — break by characters
        if (doc.getTextWidth(w) <= width) { line = w; return; }
        let chunk = '';
        for (const ch of w) {
          if (doc.getTextWidth(chunk + ch) <= width) { chunk += ch; }
          else { if (chunk) out.push(chunk); chunk = ch; }
        }
        line = chunk;
      };
      words.forEach(pushWord);
      out.push(line);
    });
    return out;
  }

  // Lay out option-value tokens with wrapping. The selected token renders as a
  // filled orange chip with reversed (white) text; the rest are plain grey.
  // If draw=false, only measures (advances must match draw exactly).
  function tokensLayout(doc, tokens, x, yTop, width, pt, factor, draw) {
    doc.setFontSize(pt);
    const gap = doc.getTextWidth(' ') * 1.8;
    const lineHmm = lh(pt, factor);
    const textH = pt * PT2MM;
    const padX = 1.4, padY = 0.8, radius = 0.8;
    let cx = x, lines = 1;
    tokens.forEach((tok) => {
      // set weight BEFORE measuring so advance matches the rendered glyph width
      doc.setFont('helvetica', tok.selected ? 'bold' : 'normal');
      const tw = doc.getTextWidth(tok.text);
      const adv = tok.selected ? tw + padX * 2 : tw;
      if (cx > x && cx + adv > x + width) { cx = x; lines++; }
      const baseline = yTop + lineHmm * lines - lineHmm * 0.30;
      if (draw) {
        if (tok.selected) {
          const rectTop = baseline - textH * 0.80 - padY;
          const rectH = textH + padY * 2;
          doc.setFillColor(KODA_ORANGE[0], KODA_ORANGE[1], KODA_ORANGE[2]);
          doc.roundedRect(cx, rectTop, adv, rectH, radius, radius, 'F');
          doc.setTextColor(255, 255, 255);
          doc.text(tok.text, cx + padX, baseline);
        } else {
          doc.setTextColor(tok.color[0], tok.color[1], tok.color[2]);
          doc.text(tok.text, cx, baseline);
        }
      }
      cx += adv + gap;
    });
    return { lines, height: lines * lineHmm };
  }

  // ── Image helpers ──────────────────────────────────────────────────────────
  // Product image: fixed box, pre-cropped to cover in the browser, so fill exactly.
  function drawCoverImage(doc, img, x, y, w, h) {
    if (img && img.dataUrl) {
      doc.addImage(img.dataUrl, img.fmt || 'JPEG', x, y, w, h, undefined, 'FAST');
    } else {
      // harness placeholder
      doc.setFillColor(232, 232, 235); doc.rect(x, y, w, h, 'F');
      doc.setDrawColor(208, 208, 213); doc.setLineWidth(0.2); doc.rect(x, y, w, h);
      doc.setFontSize(8); doc.setTextColor(150, 150, 155); doc.setFont('helvetica', 'normal');
      doc.text(img && img.label ? img.label : 'image', x + w / 2, y + h / 2, { align: 'center' });
    }
  }
  // fit an aspect (w/h) inside a box, preserving ratio (no crop)
  function fitContain(aspect, maxW, maxH) {
    aspect = aspect || 1.4;
    let w = maxW, h = w / aspect;
    if (h > maxH) { h = maxH; w = h * aspect; }
    return { w, h };
  }
  // Contain (no crop): scale to fit maxW/maxH by natural aspect, centre in maxW.
  function drawContainImage(doc, img, x, y, maxW, maxH) {
    const fit = fitContain(img && img.aspect ? img.aspect : 1.4, maxW, maxH);
    const w = fit.w, h = fit.h;
    const ox = x + (maxW - w) / 2;
    if (img && img.dataUrl) {
      doc.addImage(img.dataUrl, img.fmt || 'JPEG', ox, y, w, h, undefined, 'FAST');
    } else {
      doc.setFillColor(240, 240, 243); doc.rect(ox, y, w, h, 'F');
      doc.setDrawColor(220, 220, 224); doc.setLineWidth(0.2); doc.rect(ox, y, w, h);
      doc.setFontSize(7.5); doc.setTextColor(155, 155, 160); doc.setFont('helvetica', 'normal');
      doc.text(img && img.label ? img.label : 'drawing', ox + w / 2, y + h / 2, { align: 'center' });
    }
    return h;
  }

  // ── Header / footer ────────────────────────────────────────────────────────
  function drawHeader(doc, data, cfg, cont) {
    let headerBottomY = 14;
    let logoBox = null;
    if (data.logo && data.logo.dataUrl) {
      let w = 45, h = w / (data.logo.aspect || 3);
      if (h > 25) { h = 25; w = h * (data.logo.aspect || 3); }
      doc.addImage(data.logo.dataUrl, data.logo.fmt || 'PNG', L.margin, 10, w, h, undefined, 'FAST');
      logoBox = { x: L.margin, y: 10, w, h };
      headerBottomY = 10 + h + 3;
    } else {
      // vector KODA wordmark stand-in
      doc.setFont('helvetica', 'bold'); doc.setFontSize(26);
      doc.setTextColor(KODA_ORANGE[0], KODA_ORANGE[1], KODA_ORANGE[2]);
      doc.text('KODA', L.margin, 20);
      logoBox = { x: L.margin, y: 11, w: doc.getTextWidth('KODA'), h: 10 };
      headerBottomY = 22;
    }
    // clickable link over the logo → website
    if (cfg && cfg.logoLink && logoBox) {
      doc.link(logoBox.x, logoBox.y, logoBox.w, logoBox.h, { url: cfg.logoLink });
    }
    doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
    doc.text('Specification Sheet' + (cont ? ' (cont.)' : ''), L.margin, headerBottomY + 2);
    const lineY = headerBottomY + 8;
    doc.setDrawColor(KODA_ORANGE[0], KODA_ORANGE[1], KODA_ORANGE[2]); doc.setLineWidth(0.5);
    doc.line(L.margin, lineY, L.pageW - L.margin, lineY);
    return lineY + 10; // contentY
  }

  function drawFooterAll(doc, cfg) {
    const total = doc.internal.getNumberOfPages();
    const fy = L.pageH - 17;
    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      doc.setDrawColor(KODA_ORANGE[0], KODA_ORANGE[1], KODA_ORANGE[2]); doc.setLineWidth(0.5);
      doc.line(L.margin, fy - 4, L.pageW - L.margin, fy - 4);
      doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 100, 100);
      cfg.footerAddress.forEach((ln, k) => doc.text(ln, L.pageW / 2, fy + k * 4, { align: 'center' }));
    }
  }

  // ── Column 1: product images (crop-to-fill, left aligned) ──────────────────
  function drawCol1(doc, data, cfg, contentY) {
    const imgs = (data.images || []).slice(0, cfg.col1MaxImages);
    let y = contentY;
    imgs.forEach((im, i) => {
      drawCoverImage(doc, Object.assign({ label: 'image ' + (i + 1) }, im), L.col1X, y, L.col1.w, cfg.col1ImgH);
      // clickable link over the product image → current product/variant URL
      if (data.pageUrl) doc.link(L.col1X, y, L.col1.w, cfg.col1ImgH, { url: data.pageUrl });
      y += cfg.col1ImgH + 4;
    });
    return y;
  }

  // ── Column 2: title, SKU, description, dimensions ──────────────────────────
  function drawCol2(doc, data, cfg, contentY) {
    const x = L.col2X, w = L.col2.w;
    let y = contentY;

    // title
    doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(20, 20, 20);
    const titleLines = wrapHard(doc, (data.title || '').replace(/&amp;/g, '&').replace(/&#39;/g, "'"), w);
    const titleLH = lh(13, 1.3);
    titleLines.forEach((ln, i) => doc.text(ln, x, y + titleLH * (i + 1) - titleLH * 0.2));
    y += titleLines.length * titleLH + 4;

    // brand / designer mark — under the product name, before the SKU
    if (data.brandImage && data.brandImage.dataUrl) {
      const fit = fitContain(data.brandImage.aspect || 2.5, 42, 9);
      doc.addImage(data.brandImage.dataUrl, data.brandImage.fmt || 'PNG', x, y, fit.w, fit.h, undefined, 'FAST');
      y += fit.h + 3;
    } else if (data.brand) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(20, 20, 20);
      doc.text(data.brand, x, y + 4); y += 7;
    }

    // SKU (7pt, hard wrap so no-delimiter strings break)
    if (data.sku) {
      doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(130, 130, 130);
      const skuLines = wrapHard(doc, String(data.sku).replace(/\|/g, '\n'), w);
      const skuLH = lh(7, 1.35);
      skuLines.forEach((ln, i) => doc.text(ln, x, y + skuLH * (i + 1) - skuLH * 0.2));
      y += skuLines.length * skuLH + 3;
    }

    // separator
    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.3);
    doc.line(x, y, x + w, y); y += 5;

    // description (capped, word-boundary + …), clamped to leave room for dim image
    if (data.description) {
      doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
      const dImgH = data.dimensionImage ? 55 : 0; // reserve
      const descLH = lh(8, 1.35);
      const roomForDescMm = (L.maxY - y) - (dImgH + (dImgH ? 8 : 0));
      const maxDescLines = Math.max(3, Math.floor(roomForDescMm / descLH));
      let lines = wrapHard(doc, truncateSmart(data.description, cfg.descMaxChars), w * 0.95);
      if (lines.length > maxDescLines) {
        lines = lines.slice(0, maxDescLines);
        lines[lines.length - 1] = lines[lines.length - 1].replace(/[\s,.;:–\-]+$/, '') + '…';
      }
      lines.forEach((ln) => { doc.text(ln, x, y + descLH * 0.8); y += descLH; });
      y += 5;
    }

    // dimensions drawing (contain, no crop) — flows directly after the description
    if (data.dimensionImage) {
      doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(120, 120, 120);
      doc.text('DIMENSIONS', x, y); y += 3;
      const h = drawContainImage(doc, data.dimensionImage, x, y, w * 0.85, 55);
      y += h + 4;
    }
    return y;
  }

  // ── Column 3 content model → measurable / drawable blocks ──────────────────
  // Build config blocks (all-options with highlight) and tech grid items.
  function buildCol3Model(doc, data, cfg) {
    return {
      config: data.config || [],
      tech: (data.tech || []).filter((t) => (t.label || '').toUpperCase() !== 'BRAND'),
      brand: data.brand || null,
      brandImage: data.brandImage || null,
    };
  }

  // Measure a single config group's height (label row + option value lines).
  function measureConfigGroup(doc, group, cfg) {
    const w = L.col3W;
    const hasSwatch = cfg.showAllOptions && group.options.some((o) => o.selected && o.swatch);
    const labelRowH = hasSwatch ? 8 : lh(8, 1.3) + 1;
    let valuesH;
    if (cfg.showAllOptions) {
      const toks = group.options.map((o) => ({ text: o.value, selected: !!o.selected, color: cfg.unselectedColor }));
      valuesH = tokensLayout(doc, toks, L.col3X, 0, w, 8, CFG_VAL_FACTOR, false).height;
    } else {
      const sel = group.options.find((o) => o.selected) || group.options[0] || { value: '' };
      const lines = wrapHard(doc, sel.value, w);
      valuesH = lines.length * lh(8, 1.3);
    }
    return labelRowH + valuesH + 3; // + divider gap
  }

  function drawConfigGroup(doc, group, cfg, x, yTop) {
    const w = L.col3W;
    let y = yTop;
    const sel = group.options.find((o) => o.selected);
    const hasSwatch = cfg.showAllOptions && sel && sel.swatch;

    // label
    doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(60, 60, 60);
    doc.text(group.label, x, y + lh(8, 1.3) * 0.8);
    if (hasSwatch) {
      drawCoverImage(doc, sel.swatch, x + w - 8, y, 8, 8);
      y += 8;
    } else {
      y += lh(8, 1.3) + 1;
    }

    // values
    if (cfg.showAllOptions) {
      const toks = group.options.map((o) => ({
        text: o.value,
        selected: !!o.selected,
        color: cfg.unselectedColor,
      }));
      const r = tokensLayout(doc, toks, x, y, w, 8, CFG_VAL_FACTOR, true);
      y += r.height;
    } else {
      const s = sel || group.options[0] || { value: '' };
      doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(30, 30, 30);
      const lines = wrapHard(doc, s.value, w);
      lines.forEach((ln) => { doc.text(ln, x, y + lh(8, 1.3) * 0.8); y += lh(8, 1.3); });
    }

    // divider
    y += 1;
    doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.2);
    doc.line(x, y, x + w, y);
    return y + 2;
  }

  // Measure tech grid height for a given line factor + charSpace.
  function measureTech(doc, items, factor, charSpace) {
    const gridGap = 6;
    const colW = (L.col3W - gridGap) / 2;
    let gy = [0, 0];
    items.forEach((it, i) => {
      const side = i % 2;
      doc.setFontSize(8);
      const lblLines = wrapHard(doc, it.label, colW).length;
      const valLines = it.value ? wrapHard(doc, it.value, colW).length : 0;
      gy[side] += lblLines * lh(8, factor) + valLines * lh(8, factor) + 4;
    });
    return Math.max(gy[0], gy[1]);
  }

  // Draw a slice of tech items (from..to) as a 2-col grid at x,yTop. Returns bottom y.
  function drawTechGrid(doc, items, x, yTop, factor, charSpace, colCount) {
    colCount = colCount || 2;
    const gridGap = 6;
    const colW = (L.col3W - gridGap * (colCount - 1)) / colCount;
    // when continued on page 2 we may pass a wider area via items only; keep col3W for page1
    const totalW = colCount === 2 ? L.col3W : L.textWidth;
    const cW = (totalW - gridGap * (colCount - 1)) / colCount;
    const gx = []; for (let c = 0; c < colCount; c++) gx.push(x + c * (cW + gridGap));
    const gy = new Array(colCount).fill(yTop);
    const opt = charSpace ? { charSpace } : undefined;
    items.forEach((it, i) => {
      const side = i % colCount;
      let y = gy[side];
      doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
      wrapHard(doc, it.label, cW).forEach((ln) => { doc.text(ln, gx[side], y, opt); y += lh(8, factor); });
      if (it.value) {
        doc.setFont('helvetica', 'normal'); doc.setTextColor(70, 70, 70);
        wrapHard(doc, it.value, cW).forEach((ln) => { doc.text(ln, gx[side], y, opt); y += lh(8, factor); });
      }
      gy[side] = y + 4;
    });
    return Math.max.apply(null, gy);
  }

  function drawSectionHead(doc, text, x, y, brandRight, brandImage, koda) {
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
    doc.text(text, x, y);
    if (brandRight) {
      if (brandImage && brandImage.dataUrl) {
        let w = 25, h = w / (brandImage.aspect || 2.5); if (h > 10) { h = 10; w = h * (brandImage.aspect || 2.5); }
        doc.addImage(brandImage.dataUrl, brandImage.fmt || 'PNG', x + L.col3W - w, y - 5, w, h, undefined, 'FAST');
      } else {
        doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(20, 20, 20);
        doc.text(brandRight, x + L.col3W, y, { align: 'right' });
      }
    }
    y += 4;
    doc.setDrawColor(koda ? KODA_ORANGE[0] : 180, koda ? KODA_ORANGE[1] : 180, koda ? KODA_ORANGE[2] : 180);
    doc.setLineWidth(0.3); doc.line(x, y, x + L.col3W, y);
    return y + 4;
  }

  // ── Column 3 orchestration: config + tech with shrink-then-paginate ────────
  function drawCol3(doc, data, cfg, contentY) {
    const x = L.col3X;
    const m = buildCol3Model(doc, data, cfg);

    // 1) config first (always page 1 unless it alone overflows massively)
    let y = drawSectionHead(doc, 'SELECTED CONFIGURATION', x, contentY, null, null, false);
    // measure config total
    let cfgH = 0; m.config.forEach((g) => { cfgH += measureConfigGroup(doc, g, cfg); });
    // draw config groups, paginating if config itself overflows page 1
    const overflowConfig = [];
    for (const g of m.config) {
      const gh = measureConfigGroup(doc, g, cfg);
      if (y + gh > L.maxY && overflowConfig.length === 0 && (L.maxY - y) < gh) {
        overflowConfig.push(g); continue;
      }
      if (overflowConfig.length) { overflowConfig.push(g); continue; }
      y = drawConfigGroup(doc, g, cfg, x, y);
    }

    // 2) technical details
    const techHeadH = 8;
    const spacer = 4;
    let plan = { mode: 'onepage', factor: cfg.techLineFactorFull, charSpace: 0, page1: m.tech, page2: [], moveHeadToP2: false };

    const availAfterCfg = L.maxY - (y + spacer);
    const naturalTechH = measureTech(doc, m.tech, cfg.techLineFactorFull, 0) + techHeadH;

    if (overflowConfig.length) {
      // config already spilled; everything tech goes to page 2 as well
      plan = { mode: 'paginate', factor: cfg.techLineFactorFull, charSpace: 0, page1: [], page2: m.tech, moveHeadToP2: true };
    } else if (naturalTechH <= availAfterCfg) {
      plan.page1 = m.tech; // fits as-is
    } else {
      const overflowMm = naturalTechH - availAfterCfg;
      const tightH = measureTech(doc, m.tech, cfg.techLineFactorTight, cfg.techCharSpaceTight) + techHeadH;
      if (overflowMm <= cfg.slightOverflowMm && tightH <= availAfterCfg) {
        // compress to keep on one page
        plan = { mode: 'compress', factor: cfg.techLineFactorTight, charSpace: cfg.techCharSpaceTight, page1: m.tech, page2: [], moveHeadToP2: false };
      } else {
        // paginate: fit as many tech items on page 1 as possible (2-col), rest to page 2
        const gridGap = 6, colW = (L.col3W - gridGap) / 2;
        const gy = [y + spacer + techHeadH, y + spacer + techHeadH];
        const p1 = [], p2 = [];
        let broken = false;
        m.tech.forEach((it, i) => {
          const side = i % 2;
          const itH = (wrapHard(doc, it.label, colW).length + (it.value ? wrapHard(doc, it.value, colW).length : 0)) * lh(8, cfg.techLineFactorFull) + 4;
          if (!broken && gy[side] + itH <= L.maxY) { p1.push(it); gy[side] += itH; }
          else { broken = true; p2.push(it); }
        });
        plan = { mode: 'paginate', factor: cfg.techLineFactorFull, charSpace: 0, page1: p1, page2: p2, moveHeadToP2: p1.length === 0 };
      }
    }

    // draw tech header + page-1 slice (unless header moves to page 2)
    if (!plan.moveHeadToP2) {
      y += spacer;
      y = drawSectionHead(doc, 'TECHNICAL DETAILS', x, y, null, null, true);
      y = drawTechGrid(doc, plan.page1, x, y, plan.factor, plan.charSpace, 2);
    }

    // page 2 if needed
    if (plan.page2.length || overflowConfig.length || plan.moveHeadToP2) {
      doc.addPage();
      const c2 = drawHeader(doc, data, cfg, true);
      let y2 = c2;
      const fullX = L.margin;
      // product name + SKU so a printed page 2 stays tied to its product
      if (data.title) {
        doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(20, 20, 20);
        wrapHard(doc, (data.title || '').replace(/&amp;/g, '&').replace(/&#39;/g, "'"), L.textWidth)
          .forEach((ln) => { doc.text(ln, fullX, y2); y2 += lh(11, 1.3); });
      }
      if (data.sku) {
        doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(130, 130, 130);
        wrapHard(doc, String(data.sku).replace(/\|/g, '  '), L.textWidth)
          .forEach((ln) => { doc.text(ln, fullX, y2); y2 += lh(8, 1.35); });
      }
      if (data.title || data.sku) y2 += 5;
      if (overflowConfig.length) {
        y2 = drawSectionHead(doc, 'SELECTED CONFIGURATION (continued)', fullX, y2, null, null, false);
        for (const g of overflowConfig) y2 = drawConfigGroup(doc, g, cfg, fullX, y2);
        y2 += 4;
      }
      // technical details on page 2 span full width, 3 columns
      y2 = drawSectionHeadWide(doc, plan.moveHeadToP2 ? 'TECHNICAL DETAILS' : 'TECHNICAL DETAILS (continued)', fullX, y2, null, null);
      drawTechGrid(doc, plan.page2, fullX, y2, cfg.techLineFactorFull, 0, 3);
    }
    return y;
  }

  function drawSectionHeadWide(doc, text, x, y, brandRight, brandImage) {
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
    doc.text(text, x, y);
    if (brandImage && brandImage.dataUrl) {
      let w = 25, h = w / (brandImage.aspect || 2.5); if (h > 10) { h = 10; w = h * (brandImage.aspect || 2.5); }
      doc.addImage(brandImage.dataUrl, brandImage.fmt || 'PNG', x + L.textWidth - w, y - 5, w, h, undefined, 'FAST');
    } else if (brandRight) {
      doc.setFontSize(12); doc.text(brandRight, x + L.textWidth, y, { align: 'right' });
    }
    y += 4;
    doc.setDrawColor(KODA_ORANGE[0], KODA_ORANGE[1], KODA_ORANGE[2]); doc.setLineWidth(0.3);
    doc.line(x, y, x + L.textWidth, y);
    return y + 4;
  }

  // ── Public entry ───────────────────────────────────────────────────────────
  function render(doc, data, userCfg) {
    const cfg = Object.assign({}, DEFAULTS, userCfg || {});
    const contentY = drawHeader(doc, data, cfg, false);
    drawCol1(doc, data, cfg, contentY);
    drawCol2(doc, data, cfg, contentY);
    drawCol3(doc, data, cfg, contentY);
    drawFooterAll(doc, cfg);
    return doc;
  }

  return { render, truncateSmart, wrapHard, L, DEFAULTS };
});

/* ===========================================================================
 * koda-variant-sheet.browser.js  (glue — appended after koda-sheet-core.js)
 * DOM scraping + image compression + jsPDF wiring for the KODA spec sheet.
 * The pure layout lives in KodaSheet.render (see the core section above).
 *
 * >>> SELECTORS BELOW MIRROR THE ORIGINAL FILE. Verify against the live theme
 *     the first time; the layout is proven, the scraping needs a real page. <<<
 * ======================================================================== */
(function () {
  'use strict';

  // ── Tunables you may want to touch ──────────────────────────────────────
  const OPTS = {
    SHOW_ALL_OPTIONS: true,   // list every option per group + highlight selected
    IMG_DPI: 150,             // print resolution for embedded images
    JPEG_QUALITY: 0.82,       // product image / swatch compression
    PROD_IMG_MM: { w: 70, h: 62 }, // must match core col1.w / col1ImgH
    LOGO_LINK: 'https://kodalighting.com/', // header logo → website (switch to .com.au if that's the live domain)
    FILE_PREFIX: 'KODA-Variant-Sheet-',
    // UTM tags added to every link in the PDF so clicks back to the site are attributable
    UTM: { source: 'spec-sheet', medium: 'pdf', campaign: 'variant-sheet' },
  };
  const MM2PX = (mm) => Math.round((mm * OPTS.IMG_DPI) / 25.4);

  // Merge UTM params onto a URL, preserving any existing query (e.g. ?variant=).
  function addUTM(rawUrl, extra) {
    try {
      const u = new URL(rawUrl, window.location.origin);
      const params = Object.assign({
        utm_source: OPTS.UTM.source,
        utm_medium: OPTS.UTM.medium,
        utm_campaign: OPTS.UTM.campaign,
      }, extra || {});
      Object.keys(params).forEach((k) => { if (params[k]) u.searchParams.set(k, params[k]); });
      return u.toString();
    } catch (e) { return rawUrl; }
  }

  // ── Image helpers (load + downscale + crop) ─────────────────────────────
  function loadImage(url) {
    return new Promise((resolve) => {
      if (!url) return resolve(null);
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }
  // crop-to-fill a fixed mm box → compressed JPEG (opaque photos: product, swatch)
  async function coverJPEG(url, wMM, hMM) {
    const img = await loadImage(url);
    if (!img) return null;
    const cw = MM2PX(wMM), ch = MM2PX(hMM);
    const c = document.createElement('canvas'); c.width = cw; c.height = ch;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, cw, ch);
    const srcA = img.width / img.height, dstA = cw / ch;
    let sw, sh, sx, sy;
    if (srcA > dstA) { sh = img.height; sw = sh * dstA; sx = (img.width - sw) / 2; sy = 0; }
    else { sw = img.width; sh = sw / dstA; sx = 0; sy = (img.height - sh) / 2; }
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
    try { return { dataUrl: c.toDataURL('image/jpeg', OPTS.JPEG_QUALITY), fmt: 'JPEG' }; }
    catch (e) { return null; }
  }
  // downscale, keep aspect, no crop → PNG (line art / logos with transparency)
  async function containPNG(url, maxWMM) {
    const img = await loadImage(url);
    if (!img) return null;
    const maxPx = MM2PX(maxWMM);
    const scale = Math.min(1, maxPx / img.width);
    const cw = Math.max(1, Math.round(img.width * scale));
    const ch = Math.max(1, Math.round(img.height * scale));
    const c = document.createElement('canvas'); c.width = cw; c.height = ch;
    const ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0, cw, ch);
    try { return { dataUrl: c.toDataURL('image/png'), fmt: 'PNG', aspect: img.width / img.height }; }
    catch (e) { return null; }
  }

  // ── DOM → data model ────────────────────────────────────────────────────
  function textOf(el) {
    if (!el) return '';
    const t = document.createElement('textarea'); t.innerHTML = el.innerText || el.textContent || '';
    return t.value.replace(/\s+/g, ' ').trim();
  }

  // Find the <label> that goes with a radio input (handles for=, wrapping, siblings).
  function labelFor(fs, inp) {
    const byFor = inp.id && fs.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(inp.id) : inp.id) + '"]');
    return byFor || inp.closest('label') || (inp.parentElement && inp.parentElement.querySelector('label')) || inp.nextElementSibling;
  }

  // Resolve an option's display value. Colour/finish swatches have an image-only
  // label (no text), so we fall back through alt / aria / data and finally the
  // input's own value — which Shopify always sets to the real option value.
  function optionValue(inp, lbl) {
    const pick = (el) => el && el.innerText && el.innerText.trim();
    let v = pick(lbl && lbl.querySelector('.tooltip-color')) || pick(lbl && lbl.querySelector('.variant-button__text'));
    if (v) return v;
    const lblText = lbl ? (lbl.textContent || '').replace(/\s+/g, ' ').trim() : '';
    if (lblText) return lblText;
    const img = lbl && lbl.querySelector('img');
    const alt = img && (img.getAttribute('alt') || img.getAttribute('title'));
    if (alt && alt.trim()) return alt.trim();
    const aria = lbl && (lbl.getAttribute('aria-label') || lbl.getAttribute('title') || lbl.getAttribute('data-value'));
    if (aria && aria.trim()) return aria.trim();
    return (inp.value || '').trim();
  }

  // Grab a swatch thumbnail for the selected option (img src or CSS background-image).
  function swatchSrcOf(lbl) {
    if (!lbl) return null;
    const img = lbl.querySelector('.variant-button__image, img');
    if (img && img.src) return img.src;
    const els = [lbl].concat(Array.from(lbl.querySelectorAll('*')));
    for (const el of els) {
      const bg = getComputedStyle(el).backgroundImage;
      const m = bg && bg.match(/url\((['"]?)(.*?)\1\)/);
      if (m && m[2] && !/^data:image\/svg/.test(m[2])) return m[2];
    }
    return null;
  }

  function collectConfig(mainProductEl) {
    const groups = [];
    const seen = new Set();

    // A) simulated custom options (e.g. Model) — anchors, active = selected
    mainProductEl.querySelectorAll('fieldset.custom-option').forEach((fs) => {
      const legend = fs.querySelector('legend');
      const label = legend ? legend.innerText.replace(/:[\s\S]*/, '').trim() : '';
      if (!label || seen.has(label.toLowerCase())) return;
      const anchors = Array.from(fs.querySelectorAll('a'));
      if (!anchors.length) return;
      const options = anchors.map((a) => ({
        value: (a.dataset.optionValue || a.innerText || '').trim(),
        selected: a.classList.contains('active'),
      })).filter((o) => o.value);
      if (options.length) { groups.push({ label, options }); seen.add(label.toLowerCase()); }
    });

    // B) standard variant inputs (radios / selects / variant buttons)
    const inputs = Array.from(mainProductEl.querySelectorAll('.product-form__input'))
      .filter((el) =>
        !el.classList.contains('product-form__input--quantity') &&
        !el.classList.contains('product-form__quantity') &&
        (el.querySelector('legend') || el.querySelector('label.form__label')));

    inputs.forEach((fs) => {
      const legEl = fs.querySelector('legend');
      const lblEl = fs.querySelector('label.form__label');
      const label = (legEl || lblEl) ? (legEl || lblEl).innerText.replace(/:[\s\S]*/, '').trim() : '';
      if (!label || seen.has(label.toLowerCase())) return;

      let options = [];

      // radios / variant buttons (incl. image-only colour/finish swatches)
      const radios = Array.from(fs.querySelectorAll('input[type="radio"]'));
      if (radios.length) {
        options = radios.map((inp) => {
          const lbl = labelFor(fs, inp);
          return {
            value: optionValue(inp, lbl),
            selected: inp.checked,
            swatchSrc: inp.checked ? swatchSrcOf(lbl) : null,
          };
        }).filter((o) => o.value);
      }

      // <select>
      if (!options.length) {
        const sel = fs.querySelector('select');
        if (sel) {
          options = Array.from(sel.options)
            .filter((o) => o.value !== '')
            .map((o) => ({ value: o.text.trim(), selected: o.selected }))
            .filter((o) => o.value);
        }
      }

      if (options.length) {
        if (!options.some((o) => o.selected)) options[0].selected = true; // safety
        groups.push({ label, options });
        seen.add(label.toLowerCase());
      }
    });

    return groups;
  }

  function collectTech() {
    const wrap = document.querySelector('.custom-specifications-wrapper:not(.is-mobile)');
    if (!wrap) return [];
    const groups = Array.from(wrap.querySelectorAll('.custom-specifications-details-container .custom-specifications-group'));
    const items = [];
    groups.forEach((g) => {
      const lEl = g.querySelector('.custom-specifications-label');
      const vEls = g.querySelectorAll('.custom-specifications-value-text');
      const label = textOf(lEl);
      const value = Array.from(vEls).map(textOf).filter(Boolean).join(', ');
      if (label.toUpperCase() !== 'BRAND' && (label || value)) items.push({ label, value });
    });
    return items;
  }

  // ── Main click handler (event delegation, survives re-renders) ──────────
  document.addEventListener('click', async function (event) {
    const btn = event.target.closest('#VariantSheetButton');
    if (!btn) return;

    const cfg = window.KodaDatasheetConfig || {};
    const label = btn.querySelector('#VariantSheetButtonLabel') || btn;
    const original = label.innerText;
    label.innerText = 'Generating...';
    btn.disabled = true;

    try {
      if (!window.KodaSheet) throw new Error('KodaSheet core not loaded');
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

      const mainProductEl = document.getElementById('MainProduct-' + cfg.sectionId) || document;

      // text fields (cfg first, DOM fallback) — same sources as the original
      const domTitle = (document.querySelector('h1.product__title, h1[data-product-title], .product__title h1') || {}).innerText || '';
      const variantScript = document.querySelector('script[data-selected-variant]');
      const domSku = variantScript ? (JSON.parse(variantScript.textContent || '{}').sku || '') : '';
      const descEls = Array.from(document.querySelectorAll('#descriptionContent p, .description-content p'));
      const domDesc = descEls.map((p) => p.innerText.trim()).filter(Boolean).join(' ');

      const title = ((cfg.productTitle || domTitle) || '').replace(/&amp;/g, '&').replace(/&#39;/g, "'");
      const sku = cfg.productSku || domSku || '';
      const description = ((cfg.productDescription || domDesc) || '').replace(/\s+/g, ' ').trim();

      const config = collectConfig(mainProductEl);
      const tech = collectTech();

      // images
      const allVarImgs = Array.from(document.querySelectorAll('.product__media img, .product-media-container img'));
      const src1 = allVarImgs[0] ? allVarImgs[0].src : (cfg.productFeaturedImage || null);
      const src2 = allVarImgs[1] ? allVarImgs[1].src : null;
      const images = [];
      const img1 = await coverJPEG(src1, OPTS.PROD_IMG_MM.w, OPTS.PROD_IMG_MM.h);
      const img2 = await coverJPEG(src2, OPTS.PROD_IMG_MM.w, OPTS.PROD_IMG_MM.h);
      if (img2) images.push(img2); // original stacks image 2 then image 1
      if (img1) images.push(img1);

      const dimEl = document.querySelector('.custom-specifications-image');
      const dimensionImage = dimEl ? await containPNG(dimEl.src, 72) : null;

      const brandEl = document.querySelector('.target-collection img');
      const brandImage = brandEl ? await containPNG(brandEl.src, 25) : null;

      const logoEl = document.querySelector('.header__heading-logo');
      const logo = logoEl ? await containPNG(logoEl.src, 45) : null;

      // selected swatches (only the chosen option per group needs a swatch)
      for (const g of config) {
        const sel = g.options.find((o) => o.selected);
        if (sel && sel.swatchSrc) sel.swatch = await coverJPEG(sel.swatchSrc, 8, 8);
      }

      const data = {
        shopName: cfg.shopName || '', logo, title, sku, description,
        images, dimensionImage, brandImage,
        brand: null, config, tech,
        // product images link to the current variant URL, tagged so we can see
        // clicks that came from a product image vs the logo
        pageUrl: addUTM(window.location.href, { utm_content: 'product-image' }),
      };

      window.KodaSheet.render(doc, data, {
        showAllOptions: OPTS.SHOW_ALL_OPTIONS,
        logoLink: addUTM(OPTS.LOGO_LINK, { utm_content: 'logo' }),
      });
      doc.save(OPTS.FILE_PREFIX + (sku || cfg.productHandle || 'Koda') + '.pdf');
    } catch (err) {
      console.error('Error generating Variant Sheet:', err);
      alert('Failed to generate Variant Sheet. Please try again.');
    } finally {
      label.innerText = original;
      btn.disabled = false;
    }
  });
})();